// Static editorial case study. Public sources only; no canonical database writes.
export const caseStudy = {
  title: 'RWA: from tokenization to tradable markets',
  deck: 'Real-world assets create new ways to hold, transfer and trade familiar exposures. The commercial test is whether each product connects customer demand to reliable liquidity, an effective hedge and sustainable economics.',
  asOf: 'October 2026',
  evidenceCutoff: 'Synthesis of August–September 2026 research. Public product documentation and announcements checked on 18 October 2026; historical market snapshots are not presented as current market totals.',
  keyTakeaways: [
    {
      title: 'Start with the legal claim',
      text: 'An issuer-sponsored security, a backed certificate and a perpetual can reference the same asset while giving holders different rights, dependencies and routes to exit.',
      sourceIds: ['sec-taxonomy', 'xstocks-legal'],
    },
    {
      title: 'Evaluate each liquidity route',
      text: 'Exchange quoting, issuer conversion and institutional requests for quotes serve different needs. Each requires its own evidence on access, execution, inventory and costs.',
      sourceIds: ['xstocks-conversion', 'buidl-rfq'],
    },
    {
      title: 'Measure executable economics',
      text: 'Reported activity and displayed spreads help identify questions. Realized fills, hedge costs, funding, adverse selection and capital use determine the commercial result.',
      sourceIds: ['paxg-fees', 'hyperliquid-funding'],
    },
    {
      title: 'Keep evidence boundaries visible',
      text: 'Separate outstanding value, traded notional, open interest and transfers. Preserve the observation window, product identity, source definition and what remains unmeasured.',
      sourceIds: [],
    },
  ],
  products: [
    {
      name: 'Backed stock certificates',
      claim: 'xStocks describes its products as collateralized tracker certificates providing equity exposure without shareholder voting rights.',
      liquidity: 'Secondary trading plus issuer issuance and redemption for onboarded, whitelisted clients.',
      risk: 'Examine issuer and custody arrangements, corporate actions, conversion access and the entitlement specified in each product’s terms.',
      sourceIds: ['xstocks-legal', 'xstocks-conversion'],
    },
    {
      name: 'Equity and other RWA perpetuals',
      claim: 'A perpetual provides derivative price exposure. trade[XYZ] documents markets referencing stocks, indices, commodities and currencies on Hyperliquid.',
      liquidity: 'Trading against other participants; periodic funding helps align a perpetual with its reference price.',
      risk: 'Assess margin, liquidation, oracle behavior and hedge availability. Holding a derivative does not itself deliver underlying shares.',
      sourceIds: ['xyz-overview', 'hyperliquid-funding', 'sec-taxonomy'],
    },
    {
      name: 'Tokenized investment funds',
      claim: 'Fund interests retain their own investor and transfer conditions. The BUIDL integration uses pre-qualified investors and whitelisted counterparties.',
      liquidity: 'Securitize facilitates bilateral requests for quotes through UniswapX, alongside the fund’s own terms.',
      risk: 'Available trading hours and a quote mechanism do not guarantee a particular price, fill size or redemption turnaround.',
      sourceIds: ['buidl-rfq'],
    },
    {
      name: 'Tokenized gold',
      claim: 'PAXG terms describe fractional ownership of allocated gold, with each token corresponding to one fine troy ounce.',
      liquidity: 'Secondary trading and issuer conversion into dollars or gold, subject to account and conversion requirements.',
      risk: 'Gold-price exposure remains. Conversion fees, physical delivery constraints and settlement timing affect the practical exit.',
      sourceIds: ['paxg-terms', 'paxg-fees'],
    },
  ],
  sections: [
    {
      id: 'market-structure',
      title: 'The asset is familiar; the market structure changes',
      kind: 'analysis',
      paragraphs: [
        'RWA is a broad label covering digital representations of assets such as securities, funds and commodities. It is also used commercially for derivatives referencing those assets. The useful starting point is a product map: what is owned, who owes the obligation, where collateral sits, how a transfer becomes legally effective and how the position can be closed.',
        'The SEC staff’s taxonomy distinguishes issuer-sponsored tokenized securities from third-party custodial and synthetic structures. Its central lesson is that rights and obligations depend on the structure. Putting a record on a blockchain does not, by itself, establish direct share ownership or change the applicable securities framework. This is a taxonomy reference, not a determination of any reader’s permissions.',
      ],
      sourceIds: ['sec-taxonomy'],
    },
    {
      id: 'conversion',
      title: 'Conversion connects the token to its hedge',
      kind: 'observed',
      paragraphs: [
        'Current xStocks documentation separates the primary issuance layer from secondary trading. Primary issuance and redemption operate 24/5 for onboarded clients, while secondary trading can occur 24/7 depending on the platform. It describes both stablecoin and in-kind flows. Dividends and stock splits are handled through changes in token balances, making corporate-action treatment part of position reconciliation.',
        'Ondo describes its stock tokens as structured notes, with redemption rights tied to the underlying value and no shareholder voting rights. Its redemption documentation says immediate conversion into USDC depends on available liquidity in its stablecoin swapper. This illustrates why the exact settlement asset and conversion constraints belong alongside the quoted token price.',
        'Alpaca’s Instant Tokenization Network documents another part of that connection: securities move between an authorized participant’s brokerage account and an issuer’s account during minting and redemption. Account linking and eligibility are explicit prerequisites. The analytical implication is that conversion quality can matter as much as the exchange interface: inventory must move through a complete, permitted process before a price difference can be monetized.',
      ],
      sourceIds: ['xstocks-mechanics', 'xstocks-conversion', 'ondo-legal', 'ondo-redemption', 'alpaca-itn'],
    },
    {
      id: 'institutional-routes',
      title: 'Institutional activity spans several services',
      kind: 'observed',
      paragraphs: [
        'Public announcements establish concrete liquidity services. Securitize’s BUIDL announcement names Flowdesk, Tokka Labs and Wintermute as whitelisted subscribers to its UniswapX request-for-quote framework. It describes eligible investors obtaining bilateral quotes and settling onchain. Flow Traders separately announced continuous OTC quoting for tokenized funds, equities and commodities, with availability depending on jurisdiction and counterparty eligibility.',
        'These are distinct operating relationships: a liquidity provider quotes risk; an issuer creates the instrument; a broker or custodian handles securities; a platform distributes access. A platform investment establishes an ownership relationship, not a market-making mandate. The cited service announcements establish offerings, but they do not establish attributable trading income, market share or executable capacity for another participant.',
      ],
      sourceIds: ['buidl-rfq', 'flow-otc'],
    },
    {
      id: 'measurement',
      title: 'Build a comparable denominator before sizing demand',
      kind: 'method',
      paragraphs: [
        'Outstanding token value measures an asset stock. Turnover measures transactions over a period. Open interest measures outstanding derivative exposure. Transfers record movement between addresses and can include non-trading activity. Adding these measures produces no meaningful market total. A cumulative provider milestone also needs a definition: it may combine exchange trades, onchain transfers or issuance and redemption.',
        'A defensible comparison matches the exact underlying, product, currency and time window. It distinguishes executed quote notional from price-times-volume proxies, and calendar days from cash-market sessions. A short weekend observation cannot stand in for normal daily volume. A selected liquid-name cohort cannot establish global market share. Missing venues remain unmeasured; they should not silently become zero. This case study therefore avoids a current market-size headline.',
      ],
      sourceIds: [],
    },
    {
      id: 'economics',
      title: 'Translate activity into a complete cost model',
      kind: 'analysis',
      paragraphs: [
        'The commercial bridge is executed notional multiplied by realized net capture. Capture must account for venue fees and rebates, hedging, financing, stock borrow where needed, funding and adverse selection. A displayed full spread is not the amount a market maker earns. Orders may not fill; filled orders can be followed by unfavorable price moves; inventory may require an expensive hedge.',
        'For scale, one basis point is 0.01%: $1 billion of executed notional at one basis point of all-in net capture produces $100,000 before fixed operating costs and capital opportunity cost. This is arithmetic, not a revenue estimate. Paxos’s public schedule provides a concrete cost distinction: issuer creation and redemption charges are separate from exchange trading fees. Route-specific charges belong in the same economic model without counting overlapping income twice.',
      ],
      sourceIds: ['paxg-fees', 'hyperliquid-funding'],
    },
    {
      id: 'hours-and-hedges',
      title: 'Continuous trading creates a hedge-timing problem',
      kind: 'analysis',
      paragraphs: [
        'A token can remain transferable while its primary conversion channel or underlying cash market is unavailable. The xStocks distinction between primary and secondary hours illustrates why trading availability, conversion availability and hedge availability need separate clocks. The resulting inventory risk depends on what can actually be traded during each session, including stressed conditions and market reopenings.',
        'For APAC-linked exposures, the starting discipline is exact identity: local common shares, depositary receipts, ETFs and derivative references can carry different currency, ratio and corporate-action exposures. A thematic basket can leave substantial residual risk. An activity bucket labeled “Asian hours” describes time, not the location of traders. Proposed hedge relationships should be tested by security and session rather than inferred from a familiar ticker or theme.',
      ],
      sourceIds: ['xstocks-mechanics'],
    },
    {
      id: 'validation',
      title: 'What would strengthen the commercial case?',
      kind: 'analysis',
      paragraphs: [
        'Evaluate exchange liquidity, issuer conversion and institutional quoting as separate routes. For each, identify eligible entities and counterparties, the precise instrument, a usable hedge, professional fee terms, conversion limits and inventory financing. Then examine executed fills, subsequent price moves, stress behavior and capital consumed. Neither API visibility nor a successful small trade establishes scalable access or capacity.',
        'The strongest evidence would show repeatable customer demand and positive economics across market conditions, with incentives and concentrated event-driven activity identified separately. Legal access is product- and jurisdiction-specific; the SEC staff taxonomy and issuer distribution restrictions reinforce the need to resolve the actual claim and permitted activity. Public materials support a research agenda, while account-specific terms and attainable returns remain open questions.',
      ],
      sourceIds: ['sec-taxonomy', 'xstocks-legal', 'flow-otc'],
    },
    {
      id: 'research-method',
      title: 'Research method and evidence limits',
      kind: 'method',
      paragraphs: [
        'The August–September research was reconciled around product definitions, public documentation and explicit uncertainty. This October editorial uses freshly checked primary sources for its named examples. Reported product mechanics are distinguished from analytical implications and validation questions. Multiple summaries repeating the same announcement are one origin; agreement between AI-generated accounts is not independent corroboration.',
        'The linked sources disclose product structures and announced services. They do not independently audit reserves, prove ongoing quote quality or reveal confidential commercial terms. No firm-level profit estimate or numerical market-share claim is inferred here. Source-check dates indicate when the cited page was reviewed, not when an event occurred. Future updates should preserve this distinction and revise claims when product terms, access conditions or evidence change.',
      ],
      sourceIds: [],
    },
  ],
  sources: [
    {
      id: 'sec-taxonomy',
      title: 'Statement on Tokenized Securities',
      url: 'https://www.sec.gov/newsroom/speeches-statements/corp-fin-statement-tokenized-securities-012826-statement-tokenized-securities',
      publisher: 'SEC staff — Corporation Finance, Investment Management, Trading and Markets',
      checkedOn: '2026-10-18',
      locator: '28 January 2026; Introduction, Issuer-Sponsored Tokenized Securities, Third Party-Sponsored Tokenized Securities; footnote 2.',
      supports: 'Distinguishes issuer-sponsored, custodial and synthetic structures; describes differing rights and continued securities-law application. A staff statement with no independent legal force.',
    },
    {
      id: 'xstocks-legal',
      title: 'Product Legal Overview',
      url: 'https://docs.xstocks.fi/docs/product-legal-overview',
      publisher: 'xStocks',
      checkedOn: '2026-10-18',
      locator: 'Legal Classification; Collateral & Segregation; Distribution Restrictions.',
      supports: 'Issuer-described tracker-certificate structure, collateral arrangements, absence of shareholder voting rights and jurisdiction-dependent distribution restrictions.',
    },
    {
      id: 'xstocks-mechanics',
      title: 'How xStocks Work',
      url: 'https://docs.xstocks.fi/docs/how-xstocks-work',
      publisher: 'xStocks',
      checkedOn: '2026-10-18',
      locator: 'Primary and Secondary Markets; Corporate Actions.',
      supports: 'Primary conversion operates 24/5; secondary trading may operate 24/7 depending on platform; stablecoin/in-kind routes and rebasing for corporate actions.',
    },
    {
      id: 'xstocks-conversion',
      title: 'Issuance and Redemption',
      url: 'https://docs.xstocks.fi/docs/issuance-and-redemption',
      publisher: 'xStocks',
      checkedOn: '2026-10-18',
      locator: 'Overview; Flows; Issuer Platform.',
      supports: 'Onboarding, KYC/AML, wallet whitelisting and issuer API access for primary issuance and redemption.',
    },
    {
      id: 'ondo-legal',
      title: 'Ondo Stocks: Legal & Regulatory',
      url: 'https://docs.ondo.finance/ondo-stocks/legal-and-regulatory',
      publisher: 'Ondo Finance',
      checkedOn: '2026-10-18',
      locator: 'How are Ondo tokenized stocks structured legally?; Does this mean the tokenholder does not actually own the underlying asset?',
      supports: 'Issuer-described structured-note form, redemption entitlement tied to underlying value, and absence of shareholder voting rights.',
    },
    {
      id: 'ondo-redemption',
      title: 'Ondo Stocks: Investing & Redeeming',
      url: 'https://docs.ondo.finance/ondo-stocks/investing-and-redeeming',
      publisher: 'Ondo Finance',
      checkedOn: '2026-10-18',
      locator: 'What forms of payment can I use to purchase Ondo tokenized stocks?; Redeeming — What about redemptions?',
      supports: 'Immediate redemption into USDC depends on sufficient liquidity in the stablecoin swapper; this is a documented condition, not an independently tested capacity claim.',
    },
    {
      id: 'alpaca-itn',
      title: 'Tokenization Guide for Issuer',
      url: 'https://docs.alpaca.markets/us/docs/tokenization-guide-for-issuer',
      publisher: 'Alpaca',
      checkedOn: '2026-10-18',
      locator: 'Overview; Important Prerequisite; Handshake Process; Mint Endpoint and Workflow.',
      supports: 'Documented in-kind mint/redemption infrastructure, account linking, authorized-participant eligibility and transfer of underlying securities.',
    },
    {
      id: 'xyz-overview',
      title: 'About trade[XYZ]',
      url: 'https://docs.trade.xyz/',
      publisher: 'trade[XYZ]',
      checkedOn: '2026-10-18',
      locator: 'Product overview, Perpetuals bullet.',
      supports: 'Describes HIP-3 perpetual markets on Hyperliquid referencing stocks, equity indices, commodities and currencies.',
    },
    {
      id: 'hyperliquid-funding',
      title: 'Funding',
      url: 'https://hyperliquid.gitbook.io/hyperliquid-docs/trading/funding',
      publisher: 'Hyperliquid',
      checkedOn: '2026-10-18',
      locator: 'Overview; Technical details, HIP-3 premium formula.',
      supports: 'Periodic payments between long and short positions and the reference-price alignment mechanism. No fixed return or universal product rate is assumed.',
    },
    {
      id: 'buidl-rfq',
      title: 'Uniswap Labs and Securitize Collaborate to Unlock Liquidity Options for BlackRock’s BUIDL',
      url: 'https://investors.securitize.io/news/news-details/2026/Uniswap-Labs-and-Securitize-Collaborate-to-Unlock-Liquidity-Options-for-BlackRocks-BUIDL-02-11-2026/default.aspx',
      publisher: 'Securitize',
      checkedOn: '2026-10-18',
      locator: '11 February 2026 announcement, RFQ participants and Important Information.',
      supports: 'Announced UniswapX RFQ route with pre-qualified investors and whitelisted Flowdesk, Tokka Labs and Wintermute subscribers; no assurance of liquidity or pricing.',
    },
    {
      id: 'flow-otc',
      title: 'Flow Traders Launches OTC Offering With 24/7 Liquidity for Tokenized Assets',
      url: 'https://www.flowtraders.com/news/flow-traders-launches-otc-offering-with-247-liquidity-for-tokenized-assets/',
      publisher: 'Flow Traders',
      checkedOn: '2026-10-18',
      locator: '19 March 2026 announcement, opening paragraph and jurisdiction/counterparty availability paragraph.',
      supports: 'Announced two-way OTC liquidity in tokenized funds, equities and commodities for permissioned counterparties; availability varies by jurisdiction and eligibility.',
    },
    {
      id: 'paxg-terms',
      title: 'PAX Gold Terms and Conditions',
      url: 'https://www.paxos.com/terms-and-conditions/pax-gold-terms-conditions',
      publisher: 'Paxos',
      checkedOn: '2026-10-18',
      locator: 'Sections 2.3, 4.2–4.3, 4.11, 5.1 and 11.',
      supports: 'Allocated-gold ownership, one fine troy ounce per token, gold-price risk, customer eligibility and conversion/delivery requirements.',
    },
    {
      id: 'paxg-fees',
      title: 'PAX Gold Fees',
      url: 'https://support.paxos.com/articles/2899561282-pax-gold-fees',
      publisher: 'Paxos',
      checkedOn: '2026-10-18',
      locator: 'Creation/Destruction Fees; Redemption (Destruction) Fees; In-Kind Conversion Fee.',
      supports: 'Issuer conversion charges differ from exchange trading fees; redemption uses marginal rolling net-redemption bands and in-kind conversion has a separate charge. No rate is extrapolated to another venue.',
    },
  ],
};

export default caseStudy;
